import type { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/* ------------------------------------------------------------------ */
/*  CW-13h MEDIUM-4 — closes the validator-parity gap with backend.    */
/*                                                                     */
/*  Backend caps decimal amounts via the custom `@IsDecimalInRange`    */
/*  decorator. See, for example,                                       */
/*    src/modules/auth/dto/cross-platform-onboarding-personal.dto.ts   */
/*    src/modules/auth/dto/cross-platform-onboarding-corporate.dto.ts  */
/*    src/modules/auth/dto/register-personal.dto.ts                    */
/*    src/modules/auth/dto/register-corporate.dto.ts                   */
/*                                                                     */
/*  Both `estimatedAnnualAmount` and `expectedMonthlyTransactionVolume`*/
/*  are constrained to `min: '0', max: '100000000'`. The frontend's    */
/*  previous `Validators.pattern(/^\d+(\.\d{1,2})?$/)` accepted up to  */
/*  ~9.99e13 — out-of-range submits surfaced as a generic backend 400  */
/*  with no field-level hint. This validator mirrors the backend cap   */
/*  client-side so the user sees a precise inline error before submit. */
/* ------------------------------------------------------------------ */

/**
 * Validate that a decimal-string control value falls within `[min, max]`
 * inclusive. Returns:
 *   - `null` for empty / null / undefined values (required guards
 *     should be a separate `Validators.required`).
 *   - `{ decimalRange: { min, max, actual } }` when the parsed value
 *     falls outside the bounds.
 *   - `null` otherwise.
 *
 * The decimal pattern check (`^\d+(\.\d{1,2})?$`) is intentionally
 * left to a separate `Validators.pattern` so callers keep the existing
 * format-error key (`pattern`) for template messages.
 */
export function decimalRangeValidator(min: number, max: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    if (raw === null || raw === undefined || raw === '') {
      return null;
    }
    if (typeof raw !== 'string' && typeof raw !== 'number') {
      return null; // Non-string/number values are not our concern.
    }
    const value = typeof raw === 'string' ? Number(raw) : raw;
    if (!Number.isFinite(value)) {
      return null; // Format errors are caught by Validators.pattern.
    }
    if (value < min || value > max) {
      return { decimalRange: { min, max, actual: value } };
    }
    return null;
  };
}

/**
 * Constant mirroring backend `@IsDecimalInRange({ min: '100', max: '100000000' })`
 * for `estimatedAnnualAmount` and `expectedMonthlyTransactionVolume`.
 * Keep this in sync with the backend DTO when the cap changes.
 *
 * Bumped to 100 on 2026-05-22 (onboarding field validations bug bash) —
 * 0 / blank / sub-100 values are commercially meaningless for an
 * "estimated annual amount" or "expected monthly volume" declaration
 * and were causing seeded SOF rows that compliance had to triage.
 */
export const FINANCIAL_AMOUNT_MAX = 100_000_000;
export const FINANCIAL_AMOUNT_MIN = 100;
